import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { unwrap } from '@truecare/api-client';
import { Alert, Card } from '@truecare/ui';
import { api } from '../api';

/**
 * Read-only completion summary for the `/me` Docs *status* tab (Option B — Phase
 * 17 bugfixes). Uploads live on `/profile`; this tab is a glance-only status view.
 *
 * Reads the SAME credential + compliance catalogs the upload sections on `/profile`
 * use (shared query keys → React Query dedupes to one fetch each) and reports, at a
 * glance, where each REQUIRED document stands. Three honest states, matching the
 * real submit/activation gate (`checkCredential`/`checkCompliance`, which require
 * an APPROVED, unexpired artifact) so the notice never contradicts the Submit
 * button on `/profile`:
 *   · needs action  — not uploaded, or REJECTED/EXPIRED (caregiver must (re)upload)
 *   · under review  — uploaded + PENDING (waiting on TrueCare)
 *   · approved      — APPROVED
 * Only all-approved shows the green "ready" success; uploaded-but-pending shows an
 * info "we're reviewing" state, not a false "you're done".
 */
export function CaregiverDocsCompletionNotice() {
  const credentials = useQuery({
    queryKey: ['caregiver', 'credentials', 'catalog'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/credentials/catalog')),
  });
  const compliance = useQuery({
    queryKey: ['caregiver', 'compliance', 'catalog'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/compliance/catalog')),
  });

  if (credentials.isPending || compliance.isPending) {
    return (
      <Card padding="md">
        <p className="font-body text-sm text-ink-muted">Checking your documents…</p>
      </Card>
    );
  }
  // On error, stay silent — the sections below surface their own load errors, so a
  // second error banner would just be noise.
  if (credentials.isError || compliance.isError || !credentials.data || !compliance.data) {
    return null;
  }

  const required = [...credentials.data.items, ...compliance.data.items].filter(
    (i) => i.requiredForCaregiver,
  );
  const total = required.length;
  const statusOf = (i: (typeof required)[number]) => i.currentArtifact?.status ?? null;
  // Needs a caregiver action: never uploaded, or uploaded then rejected/expired.
  const needsAction = required.filter((i) => {
    const s = statusOf(i);
    return s === null || s === 'REJECTED' || s === 'EXPIRED';
  }).length;
  const approved = required.filter((i) => statusOf(i) === 'APPROVED').length;
  const underReview = total - needsAction - approved; // PENDING

  return (
    <Card padding="md" data-testid="caregiver-docs-completion">
      {total === 0 ? (
        <Alert tone="info" title="No documents required">
          Your selected roles don't require any document uploads.
        </Alert>
      ) : needsAction > 0 ? (
        <Alert
          tone="warning"
          title={`${approved} of ${total} required documents approved`}
        >
          {needsAction === 1 ? '1 document still needs' : `${needsAction} documents still need`}{' '}
          your attention (upload or re-upload).{' '}
          <Link
            to="/profile"
            className="font-semibold text-forest underline underline-offset-2"
            data-testid="caregiver-docs-manage-link"
          >
            Manage them in your profile →
          </Link>
        </Alert>
      ) : underReview > 0 ? (
        <Alert tone="info" title="All documents uploaded — under review">
          Every required document is on file ({approved} of {total} approved so far).
          TrueCare™ reviews each one before your account is activated.
        </Alert>
      ) : (
        <Alert tone="success" title="All required documents approved">
          Every required document is approved and on file.
        </Alert>
      )}
    </Card>
  );
}
